import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import {
  getNip07Provider,
  LocalSigner,
  Nip07Signer,
  ReadOnlySigner,
  SignerError,
  type Signer,
  type SignerMethod,
} from "@/nostr/signers"
import { npubOf, toPubkey } from "@/utils/nip19"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"
import { useNostr } from "./NostrContext"
import { useToast } from "./ToastContext"

export interface AccountRef {
  pubkey: string
  method: SignerMethod
}

interface StoredAuth {
  accounts: AccountRef[]
  current?: string
}

interface AuthContextValue {
  pubkey?: string
  npub?: string
  method?: SignerMethod
  signer?: Signer
  accounts: AccountRef[]
  /** True when the session can actually sign (extension or unlocked nsec). */
  canSign: boolean
  hasExtension: boolean
  loginWithExtension: () => Promise<void>
  loginWithNsec: (nsec: string) => Promise<void>
  loginWithNpub: (value: string) => void
  switchAccount: (pubkey: string) => Promise<void>
  logout: () => void
  forgetAccount: (pubkey: string) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function loadStoredAuth(): StoredAuth {
  const stored = readStorage<StoredAuth>(StorageKeys.account, { accounts: [] })
  const accounts = Array.isArray(stored.accounts)
    ? stored.accounts.filter(
        (account): account is AccountRef =>
          Boolean(account) && /^[0-9a-f]{64}$/.test(account.pubkey ?? "") && Boolean(account.method),
      )
    : []
  return { accounts, current: stored.current }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setSigner } = useNostr()
  const { push } = useToast()
  const [stored, setStored] = useState<StoredAuth>(() => loadStoredAuth())
  const [pubkey, setPubkey] = useState<string | undefined>(stored.current)
  const [method, setMethod] = useState<SignerMethod | undefined>(undefined)
  const [signer, setSignerState] = useState<Signer | undefined>(undefined)
  const [hasExtension] = useState(() => Boolean(getNip07Provider()))

  const persist = useCallback((next: StoredAuth) => {
    setStored(next)
    writeStorage(StorageKeys.account, next)
  }, [])

  const applySession = useCallback(
    (nextPubkey: string | undefined, nextSigner: Signer | undefined, nextMethod?: SignerMethod) => {
      setPubkey(nextPubkey)
      setSignerState(nextSigner)
      setMethod(nextMethod)
      setSigner(nextSigner)
    },
    [setSigner],
  )

  // Restore the last session once: extensions are asked silently, nsec sessions
  // are downgraded to read-only because the key is never persisted.
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      if (!stored.current) return
      const account = stored.accounts.find((item) => item.pubkey === stored.current)
      if (!account) return

      if (account.method === "nip07") {
        const provider = getNip07Provider()
        if (provider) {
          try {
            const current = await provider.getPublicKey()
            if (cancelled) return
            if (current === account.pubkey) {
              applySession(account.pubkey, new Nip07Signer(provider), "nip07")
              return
            }
          } catch {
            // the user declined the silent permission request
          }
        }
      }
      if (!cancelled) applySession(account.pubkey, new ReadOnlySigner(account.pubkey), "npub")
    }
    void restore()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const rememberAccount = useCallback(
    (account: AccountRef) => {
      const accounts = [
        account,
        ...stored.accounts.filter((item) => item.pubkey !== account.pubkey),
      ].slice(0, 10)
      persist({ accounts, current: account.pubkey })
    },
    [persist, stored.accounts],
  )

  const loginWithExtension = useCallback(async () => {
    const provider = getNip07Provider()
    if (!provider) throw new SignerError("no NIP-07 extension found in this browser")
    const nextSigner = new Nip07Signer(provider)
    const nextPubkey = await nextSigner.getPublicKey()
    rememberAccount({ pubkey: nextPubkey, method: "nip07" })
    applySession(nextPubkey, nextSigner, "nip07")
    push("success", "Signed in with your browser extension")
  }, [applySession, push, rememberAccount])

  const loginWithNsec = useCallback(
    async (nsec: string) => {
      const nextSigner = LocalSigner.fromNsec(nsec)
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "nsec" })
      applySession(nextPubkey, nextSigner, "nsec")
      push("success", "Signed in — the key stays in memory for this session only")
    },
    [applySession, push, rememberAccount],
  )

  const loginWithNpub = useCallback(
    (value: string) => {
      const nextPubkey = toPubkey(value)
      if (!nextPubkey) {
        push("error", "That is not a valid npub, nprofile or hex public key")
        return
      }
      rememberAccount({ pubkey: nextPubkey, method: "npub" })
      applySession(nextPubkey, new ReadOnlySigner(nextPubkey), "npub")
      push("info", "Browsing in read-only mode")
    },
    [applySession, push, rememberAccount],
  )

  const switchAccount = useCallback(
    async (targetPubkey: string) => {
      const account = stored.accounts.find((item) => item.pubkey === targetPubkey)
      if (!account) return
      if (account.method === "nip07") {
        const provider = getNip07Provider()
        if (provider) {
          try {
            const current = await provider.getPublicKey()
            if (current === targetPubkey) {
              persist({ ...stored, current: targetPubkey })
              applySession(targetPubkey, new Nip07Signer(provider), "nip07")
              return
            }
            push("error", "Your extension is unlocked with a different key")
          } catch (error) {
            push("error", error instanceof Error ? error.message : "The extension refused the request")
          }
        }
      }
      persist({ ...stored, current: targetPubkey })
      applySession(targetPubkey, new ReadOnlySigner(targetPubkey), "npub")
    },
    [applySession, persist, push, stored],
  )

  const logout = useCallback(() => {
    persist({ accounts: stored.accounts, current: undefined })
    applySession(undefined, undefined, undefined)
  }, [applySession, persist, stored.accounts])

  const forgetAccount = useCallback(
    (targetPubkey: string) => {
      const accounts = stored.accounts.filter((item) => item.pubkey !== targetPubkey)
      const current = stored.current === targetPubkey ? undefined : stored.current
      persist({ accounts, current })
      if (stored.current === targetPubkey) applySession(undefined, undefined, undefined)
    },
    [applySession, persist, stored],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      pubkey,
      npub: pubkey ? npubOf(pubkey) : undefined,
      method,
      signer,
      accounts: stored.accounts,
      canSign: Boolean(signer) && method !== "npub",
      hasExtension,
      loginWithExtension,
      loginWithNsec,
      loginWithNpub,
      switchAccount,
      logout,
      forgetAccount,
    }),
    [
      pubkey,
      method,
      signer,
      stored.accounts,
      hasExtension,
      loginWithExtension,
      loginWithNsec,
      loginWithNpub,
      switchAccount,
      logout,
      forgetAccount,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used inside an AuthProvider")
  return context
}
